import React from "react";

interface ModeRendererProps {
  brand: string;
  today: string;
  Copy: string;
}

export function VasarelyAirliquideMode({ brand }: ModeRendererProps) {
  const width = 794;
  const height = 1123;

  // Air Liquide translucent gradient - light blue to cyan
  const bgGradient = (
    <defs>
      <radialGradient id="airliquideGradient" cx="50%" cy="40%" r="70%">
        <stop offset="0%" style={{ stopColor: "#E0F7FA", stopOpacity: 1 }} />
        <stop offset="50%" style={{ stopColor: "#80DEEA", stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: "#00ACC1", stopOpacity: 1 }} />
      </radialGradient>
    </defs>
  );

  // Bubble grids - molecular gas particles
  const bubbleGrid: JSX.Element[] = [];
  const gridX = 16;
  const gridY = 22;
  const cellW = 45;
  const cellH = 48;
  
  for (let i = 0; i < gridX; i++) {
    for (let j = 0; j < gridY; j++) {
      const x = -360 + i * cellW + (j % 2) * 22;
      const y = -520 + j * cellH;
      const radius = 8 + Math.sin(i * 0.4 + j * 0.3) * 6;
      const opacity = 0.15 + Math.cos(i * 0.3) * 0.1;
      
      bubbleGrid.push(
        <circle
          key={`bubble-${i}-${j}`}
          cx={x}
          cy={y}
          r={radius}
          fill="none"
          stroke="#FFFFFF"
          strokeWidth={1.5}
          opacity={opacity}
        />
      );
    }
  }

  // Flow particles - ascending molecular movement
  const flowParticles: JSX.Element[] = [];
  for (let stream = 0; stream < 24; stream++) {
    const startX = -340 + stream * 28;
    const points: string[] = [];
    
    for (let p = 0; p <= 35; p++) {
      const t = p / 35;
      const x = startX + Math.sin(t * Math.PI * 4 + stream * 0.5) * 20;
      const y = -500 + t * 1000;
      points.push(`${x},${y}`);
    }
    
    flowParticles.push(
      <polyline
        key={`flow-${stream}`}
        points={points.join(" ")}
        fill="none"
        stroke="#00ACC1"
        strokeWidth={1}
        opacity={0.2}
        strokeDasharray="4,6"
      />
    );
  }

  // Translucent fields - gas diffusion layers
  const diffusionFields: JSX.Element[] = [];
  for (let layer = 0; layer < 12; layer++) {
    const y = -450 + layer * 80;
    const opacity = 0.08 + Math.sin(layer * 0.5) * 0.04;
    
    diffusionFields.push(
      <ellipse
        key={`field-${layer}`}
        cx={0}
        cy={y}
        rx={350 - layer * 15}
        ry={40 + layer * 5}
        fill="#80DEEA"
        opacity={opacity}
      />
    );
  }

  // Purity control circles - concentric quality assurance
  const purityCircles: JSX.Element[] = [];
  for (let c = 1; c <= 18; c++) {
    const radius = c * 22;
    purityCircles.push(
      <circle
        key={`purity-${c}`}
        cx={0}
        cy={0}
        r={radius}
        fill="none"
        stroke="#FFFFFF"
        strokeWidth={c % 3 === 0 ? 2 : 1}
        opacity={0.25 - c * 0.01}
      />
    );
  }

  // Invisible order nodes - precision measurement points
  const orderNodes: JSX.Element[] = [];
  const nodeRing = 7;
  const nodeRadius = 220;
  
  for (let n = 0; n < nodeRing; n++) {
    const angle = (n / nodeRing) * 2 * Math.PI;
    const nx = Math.cos(angle) * nodeRadius;
    const ny = Math.sin(angle) * nodeRadius;
    
    orderNodes.push(
      <g key={`node-${n}`}>
        <circle
          cx={nx}
          cy={ny}
          r={32}
          fill="none"
          stroke="#FFFFFF"
          strokeWidth={2.5}
          opacity={0.4}
        />
        <circle
          cx={nx}
          cy={ny}
          r={20}
          fill="none"
          stroke="#00ACC1"
          strokeWidth={2}
          opacity={0.35}
        />
        <circle
          cx={nx}
          cy={ny}
          r={8}
          fill="#80DEEA"
          opacity={0.5}
        />
      </g>
    );
  }

  // Gas molecule connections
  const moleculeConnections: JSX.Element[] = [];
  for (let n = 0; n < nodeRing; n++) {
    const angle1 = (n / nodeRing) * 2 * Math.PI;
    const angle2 = ((n + 1) / nodeRing) * 2 * Math.PI;
    const x1 = Math.cos(angle1) * nodeRadius;
    const y1 = Math.sin(angle1) * nodeRadius;
    const x2 = Math.cos(angle2) * nodeRadius;
    const y2 = Math.sin(angle2) * nodeRadius;
    
    moleculeConnections.push(
      <line
        key={`connect-${n}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="#FFFFFF"
        strokeWidth={1.5}
        opacity={0.2}
        strokeDasharray="5,5"
      />
    );
  }

  // Controlled diffusion waves - circular expansion
  const diffusionWaves: JSX.Element[] = [];
  for (let w = 0; w < 8; w++) {
    const waveR = 100 + w * 45;
    const segments = 24;
    
    for (let s = 0; s < segments; s++) {
      const angle1 = (s / segments) * 2 * Math.PI;
      const angle2 = ((s + 0.6) / segments) * 2 * Math.PI;
      const x1 = Math.cos(angle1) * waveR;
      const y1 = Math.sin(angle1) * waveR;
      const x2 = Math.cos(angle2) * waveR;
      const y2 = Math.sin(angle2) * waveR;
      
      diffusionWaves.push(
        <line
          key={`wave-${w}-${s}`}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="#00ACC1"
          strokeWidth={2}
          opacity={0.15}
        />
      );
    }
  }

  // Central purity core
  const purityCore = (
    <g>
      <circle cx={0} cy={0} r={50} fill="none" stroke="#FFFFFF" strokeWidth={3} opacity={0.5} />
      <circle cx={0} cy={0} r={35} fill="none" stroke="#80DEEA" strokeWidth={2.5} opacity={0.4} />
      <circle cx={0} cy={0} r={20} fill="#00ACC1" opacity={0.3} />
    </g>
  );

  return (
    <svg width={width} height={height} xmlns="http://www.w3.org/2000/svg">
      {bgGradient}
      <rect width={width} height={height} fill="url(#airliquideGradient)" />
      <g transform={`translate(${width / 2}, ${height / 2})`}>
        {diffusionFields}
        {flowParticles}
        {bubbleGrid}
        {purityCircles}
        {diffusionWaves}
        {moleculeConnections}
        {orderNodes}
        {purityCore}
      </g>
    </svg>
  );
}
